(function () {
  const CURRENCIES_URL = 'https://cdn.shopify.com/s/javascripts/currencies.js';
  let currencyRatesPromise = null;

  function loadCurrencyRates() {
    if (window.Currency && window.Currency.rates) {
      return Promise.resolve(window.Currency.rates);
    }
    if (!currencyRatesPromise) {
      currencyRatesPromise = new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = CURRENCIES_URL;
        script.onload = () => resolve((window.Currency && window.Currency.rates) || null);
        script.onerror = () => resolve(null);
        document.head.appendChild(script);
      });
    }
    return currencyRatesPromise;
  }

  // Reverse-engineers the shop's own money format (symbol, decimal separator, thousands
  // separator) from a sample amount already rendered through Liquid's `money` filter, so
  // JS-computed amounts match the theme's formatting instead of guessing via Intl locales
  // (which are keyed by language, not currency, and get separators wrong e.g. for EUR).
  function parseMoneyFormat(sample) {
    if (!sample) return null;

    let match = sample.match(/^([^\d]*)([\d.,\s ']*\d)([.,])(\d{2})([^\d]*)$/);
    if (match) {
      const intPart = match[2];
      const sepMatch = intPart.match(/[^\d]/);
      return {
        prefix: match[1] || '',
        suffix: match[5] || '',
        decimalSep: match[3],
        thousandsSep: sepMatch ? sepMatch[0] : '',
        hasDecimals: true,
      };
    }

    match = sample.match(/^([^\d]*)([\d.,\s ']*\d)([^\d]*)$/);
    if (match) {
      const intPart = match[2];
      const sepMatch = intPart.match(/[^\d]/);
      return {
        prefix: match[1] || '',
        suffix: match[3] || '',
        decimalSep: '',
        thousandsSep: sepMatch ? sepMatch[0] : '',
        hasDecimals: false,
      };
    }

    return null;
  }

  function formatMoney(cents, formatter) {
    const amount = Math.abs(cents) / 100;

    if (!formatter) {
      return amount.toFixed(2);
    }

    const rounded = formatter.hasDecimals ? amount.toFixed(2) : String(Math.round(amount));
    const [wholePart, decimalPart] = rounded.split('.');
    const withThousands = wholePart.replace(/\B(?=(\d{3})+(?!\d))/g, formatter.thousandsSep);
    const numberString = formatter.hasDecimals ? withThousands + formatter.decimalSep + decimalPart : withThousands;

    return formatter.prefix + numberString + formatter.suffix;
  }

  // Cart updates replace the bar's markup wholesale (innerHTML swap / replaceWith), so a
  // freshly-inserted element has no "old" width to transition from — the CSS transition
  // never gets to run. Remembering the last percent across those swaps and manually
  // stepping the fill from there (old width with no transition, forced reflow, then the
  // new width with the transition restored) is what makes it actually animate.
  let lastPercent = 0;
  // Same problem for the message: a freshly-swapped-in "complete" bar arrives with the
  // success text already baked into its markup, with no "previous" progress text of its
  // own to fall back on while the fill is still catching up. Remembering the last progress
  // message across swaps lets a newly-complete bar keep showing it during the animation
  // instead of jumping straight to (or blanking before) the success text.
  let lastProgressText = '';

  // `onWidthTransitionEnd` (if given) fires only once the width transition actually
  // finishes, so callers can defer visual state (like the success color) until the bar
  // has really reached its new position instead of flipping the instant the class changes.
  function animateFillTo(fillEl, toPercent, onWidthTransitionEnd) {
    if (fillEl) {
      const fromPercent = lastPercent;
      fillEl.style.transition = 'none';
      fillEl.style.width = fromPercent + '%';
      void fillEl.offsetWidth;
      fillEl.style.transition = '';
      requestAnimationFrame(() => {
        fillEl.style.width = toPercent + '%';
      });

      // `lastPercent` only advances once the fill has really arrived. A cart update can swap
      // the bar's markup more than once in quick succession; if the first (soon-discarded)
      // copy recorded the new percent up front, the next copy would think it was already
      // there and skip the animation (and the success state with it).
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        fillEl.removeEventListener('transitionend', handleEnd);
        lastPercent = toPercent;
        if (onWidthTransitionEnd) onWidthTransitionEnd();
      };
      function handleEnd(event) {
        if (event.propertyName === 'width') finish();
      }
      if (fromPercent === toPercent) {
        finish();
      } else {
        fillEl.addEventListener('transitionend', handleEnd);
        // transitionend doesn't fire if the transition is cancelled or the element is swapped
        setTimeout(finish, 1200);
      }
    } else {
      lastPercent = toPercent;
      if (onWidthTransitionEnd) onWidthTransitionEnd();
    }
  }

  class FreeShippingBar extends HTMLElement {
    connectedCallback() {
      const fill = this.querySelector('[data-free-shipping-bar-fill]');
      const track = this.querySelector('.free-shipping-bar__track');
      const text = this.querySelector('[data-free-shipping-bar-text]');
      const targetPercent = track ? parseFloat(track.getAttribute('aria-valuenow')) || 0 : 0;
      // The server pre-renders the --complete class (and success text) straight onto this
      // fresh markup, but the fill's width is about to be rewound and re-animated up to it
      // (see animateFillTo above). Strip both up front so the success color/message don't
      // appear before the fill even starts moving, then restore them once the animation
      // actually reaches 100%.
      const shouldBeComplete = this.classList.contains('free-shipping-bar--complete');
      this._complete = shouldBeComplete;
      if (shouldBeComplete) {
        this.classList.remove('free-shipping-bar--complete');
        if (text) text.textContent = lastProgressText;
      } else if (text) {
        lastProgressText = text.textContent;
      }
      animateFillTo(fill, targetPercent, () => {
        this.classList.toggle('free-shipping-bar--complete', shouldBeComplete);
        if (shouldBeComplete && text) text.textContent = this.dataset.successText;
      });
      this.render();
    }

    // Called when the cart drawer opens: the bar was already animated while the drawer was
    // hidden, so rewind it to zero and grow it to its value where the user can see it.
    // Step 1: call synchronously, before the drawer becomes visible, so the user never sees
    // the bar at its final value.
    resetForReplay() {
      const fill = this.querySelector('[data-free-shipping-bar-fill]');
      const text = this.querySelector('[data-free-shipping-bar-text]');
      if (!fill) return;
      fill.style.transition = 'none';
      fill.style.width = '0%';
      if (this._complete) {
        this.classList.remove('free-shipping-bar--complete');
        if (text && lastProgressText) text.textContent = lastProgressText;
      }
      lastPercent = 0;
    }

    // Step 2: once the drawer is on screen, grow the fill to its value.
    replay() {
      const fill = this.querySelector('[data-free-shipping-bar-fill]');
      const track = this.querySelector('.free-shipping-bar__track');
      const text = this.querySelector('[data-free-shipping-bar-text]');
      if (!fill || !track) return;
      const targetPercent = parseFloat(track.getAttribute('aria-valuenow')) || 0;
      const shouldBeComplete = !!this._complete;
      animateFillTo(fill, targetPercent, () => {
        this.classList.toggle('free-shipping-bar--complete', shouldBeComplete);
        if (shouldBeComplete && text) text.textContent = this.dataset.successText;
      });
    }

    render() {
      const shopCurrency = this.dataset.shopCurrency;
      const cartCurrency = this.dataset.cartCurrency;
      const thresholdCents = parseInt(this.dataset.thresholdCents, 10) || 0;
      const cartTotalCents = parseInt(this.dataset.cartTotalCents, 10) || 0;

      if (!shopCurrency || !cartCurrency || shopCurrency === cartCurrency) {
        return;
      }

      loadCurrencyRates().then((rates) => {
        if (!rates || !rates[shopCurrency] || !rates[cartCurrency]) return;
        // Currency.rates[code] is the USD value of 1 unit of that currency, so converting
        // shopCurrency -> USD -> cartCurrency means multiplying by rates[shopCurrency] and
        // dividing by rates[cartCurrency].
        const convertedThreshold = Math.round((thresholdCents * rates[shopCurrency]) / rates[cartCurrency]);
        this.update(convertedThreshold, cartTotalCents);
      });
    }

    update(thresholdCents, cartTotalCents) {
      const remaining = Math.max(thresholdCents - cartTotalCents, 0);
      const percent = thresholdCents > 0 ? Math.min((cartTotalCents / thresholdCents) * 100, 100) : 100;
      const isComplete = remaining === 0;
      this._complete = isComplete;
      const formatter = parseMoneyFormat(this.dataset.moneyFormatSample);

      const text = this.querySelector('[data-free-shipping-bar-text]');
      const fill = this.querySelector('[data-free-shipping-bar-fill]');
      const track = this.querySelector('.free-shipping-bar__track');
      const currentLabel = this.querySelector('[data-free-shipping-bar-current]');
      const thresholdLabel = this.querySelector('[data-free-shipping-bar-threshold]');

      if (text && !isComplete) {
        text.textContent = this.dataset.progressTemplate.replace('[amount]', formatMoney(remaining, formatter));
        lastProgressText = text.textContent;
      }
      animateFillTo(fill, percent, () => {
        this.classList.toggle('free-shipping-bar--complete', isComplete);
        if (isComplete && text) text.textContent = this.dataset.successText;
      });
      if (track) track.setAttribute('aria-valuenow', String(Math.round(percent)));
      if (currentLabel) currentLabel.textContent = formatMoney(cartTotalCents, formatter);
      if (thresholdLabel) thresholdLabel.textContent = formatMoney(thresholdCents, formatter);
    }
  }

  if (!customElements.get('free-shipping-bar')) {
    customElements.define('free-shipping-bar', FreeShippingBar);
  }
})();
